import {
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { createPortal } from 'react-dom'
import MoreHorizRoundedIcon from '@mui/icons-material/MoreHorizRounded'
import { OverflowTip } from './Hover/OverflowTip'
import { Pressable } from './Pressable'
import { useNls } from '../../nls/nlsProvider'
import './mobileSubtabs.css'

export interface MobileTabBarMoreItem {
  label: string
  ariaLabel?: string
  icon?: ReactNode
  activeIcon?: ReactNode
  isActive?: boolean
  unread?: boolean
  onClick: () => void
  testId?: string
}

/**
 * Shared mobile tab bar, extracted from the helper app's bottom nav so the
 * helper and member apps render the exact same tab chrome. A bar hosts one
 * primary row and, on screens that need one, a secondary row stacked directly
 * above it in the same visual format.
 */

export type MobileTabItem<K extends string = string> = {
  key: K
  label: string
  /** Icon rendered when the tab is inactive. */
  icon?: ReactNode
  /** Icon rendered when the tab is active. Falls back to `icon`. */
  activeIcon?: ReactNode
  /** Show the unread dot. Ignored for the active tab — it is being looked at. */
  unread?: boolean
  testId?: string
  /** Optional press-and-hold action for a primary tab. */
  onLongPress?: () => void
  /** Optional count badge per item (e.g. for subtabs). */
  count?: number
}

export const MobileTabMoreButton = ({
  more,
  scrollMode = false,
  slim = false,
  compact = false,
  unreadAriaLabel,
}: {
  more: MobileTabBarMoreItem
  scrollMode?: boolean
  slim?: boolean
  compact?: boolean
  unreadAriaLabel?: string
}) => {
  return (
    <Pressable
      key="more"
      role="tab"
      onClick={more.onClick}
      aria-selected={more.isActive}
      aria-current={more.isActive ? 'page' : undefined}
      aria-label={more.ariaLabel ?? more.label}
      data-testid={more.testId ?? 'mobile-tab-more'}
      className={`${scrollMode ? 'flex-[1_0_auto] min-w-[5.3rem] snap-center' : 'flex-1 min-w-0'} ${
        slim ? 'h-11 gap-0' : compact ? 'h-12' : 'h-14'
      } flex flex-col items-center justify-center group`}
    >
      <span
        className={`relative inline-flex items-center justify-center ${slim ? 'h-7 w-12' : 'h-9 w-14'} rounded-full transition-all duration-200 ease-[cubic-bezier(0.16,1,0.3,1)] ${
          more.isActive
            ? 'bg-[var(--accent-soft)] text-[var(--accent-strong)] border border-[color-mix(in_srgb,var(--accent)_40%,transparent)] scale-100'
            : 'text-[var(--muted)] group-hover:text-[var(--text-strong)] scale-95'
        }`}
      >
        <span className="inline-flex items-center justify-center" style={{ filter: 'none' }}>
          {more.isActive
            ? (more.activeIcon ?? more.icon ?? <MoreHorizRoundedIcon fontSize="small" style={{ fontSize: '22px' }} />)
            : (more.icon ?? <MoreHorizRoundedIcon fontSize="small" style={{ fontSize: '22px' }} />)}
        </span>
        {more.unread ? (
          <span
            className="absolute -top-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-[var(--danger)] ring-1 ring-[var(--surface-card)]"
            aria-label={unreadAriaLabel ?? 'unread'}
            data-testid={more.testId ? `${more.testId}-unread` : 'mobile-tab-more-unread'}
          />
        ) : null}
      </span>
      <OverflowTip label={more.label}>
        <span
          className={`max-w-full truncate px-0.5 text-[11px] tracking-normal ${
            more.isActive ? 'font-semibold text-[var(--text-strong)]' : 'font-normal text-[var(--muted)] group-hover:text-[var(--text-strong)]'
          }`}
        >
          {more.label}
        </span>
      </OverflowTip>
    </Pressable>
  )
}

export const MobileTabRow = <K extends string>({
  items,
  activeKey,
  onChange,
  ariaLabel,
  testId,
  compact = false,
  slim = false,
  overflowStrategy = 'wrap',
  className,
  variant = 'pills',
  placement = 'top',
  more,
}: {
  items: Array<MobileTabItem<K>>
  activeKey?: K
  onChange: (key: K) => void
  ariaLabel: string
  testId?: string
  /** Slightly shorter buttons — used when two rows are stacked. */
  compact?: boolean
  /** Compact sub-navigation with a smaller pill and no label gap. */
  slim?: boolean
  overflowStrategy?: 'wrap' | 'scroll'
  /** Surface-specific row treatment without duplicating tab semantics. */
  className?: string
  /** Visual variant: 'pills' (default) or 'underline' for subtab strip. */
  variant?: 'pills' | 'underline'
  /** Placement: 'top' (default) or 'bottom' for sticky subtab bar (EM-D20). */
  placement?: 'top' | 'bottom'
  /** Optional More item rendered as the final fixed tab in the primary row. */
  more?: MobileTabBarMoreItem
}) => {
  const { t } = useNls()
  const unreadDotAria = t('NLSCommonTabUnreadDotAria') ?? 'unread'
  const scrollMode = overflowStrategy === 'scroll'
  const tabRefs = useRef<(HTMLElement | null)[]>([])

  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const longPressStart = useRef<{ x: number; y: number } | null>(null)
  const longPressSelection = useRef<{ element: HTMLElement; userSelect: string; webkitUserSelect: string; webkitTouchCallout: string } | null>(null)
  const suppressClick = useRef(false)

  const suppressSelection = (element: HTMLElement) => {
    longPressSelection.current = {
      element,
      userSelect: element.style.getPropertyValue('user-select'),
      webkitUserSelect: element.style.getPropertyValue('-webkit-user-select'),
      webkitTouchCallout: element.style.getPropertyValue('-webkit-touch-callout'),
    }
    element.style.setProperty('user-select', 'none')
    element.style.setProperty('-webkit-user-select', 'none')
    element.style.setProperty('-webkit-touch-callout', 'none')
  }

  const restoreSelection = () => {
    const previous = longPressSelection.current
    if (!previous) return
    const restore = (property: string, value: string) => {
      if (value) previous.element.style.setProperty(property, value)
      else previous.element.style.removeProperty(property)
    }
    restore('user-select', previous.userSelect)
    restore('-webkit-user-select', previous.webkitUserSelect)
    restore('-webkit-touch-callout', previous.webkitTouchCallout)
    longPressSelection.current = null
  }

  const clearLongPress = () => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current)
      longPressTimer.current = null
    }
    longPressStart.current = null
    restoreSelection()
    requestAnimationFrame(() => {
      suppressClick.current = false
    })
  }

  useEffect(() => () => {
    if (longPressTimer.current) clearTimeout(longPressTimer.current)
    restoreSelection()
  }, [])

  const handleTabKeyDown = (e: React.KeyboardEvent, index: number) => {
    let nextIndex = -1
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
      e.preventDefault()
      nextIndex = (index + 1) % items.length
    } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
      e.preventDefault()
      nextIndex = (index - 1 + items.length) % items.length
    } else if (e.key === 'Home') {
      e.preventDefault()
      nextIndex = 0
    } else if (e.key === 'End') {
      e.preventDefault()
      nextIndex = items.length - 1
    }

    if (nextIndex >= 0 && nextIndex < items.length) {
      tabRefs.current[nextIndex]?.focus()
      onChange(items[nextIndex].key)
    }
  }

  const isBottom = placement === 'bottom'

  if (variant === 'underline') {
    return (
      <div
        className={`mobileTabRow mobileTabRow--underline ${isBottom ? 'mobileTabRow--placement-bottom border-t border-[var(--border)]' : 'border-b border-[var(--border)]'} ${scrollMode ? 'mobileTabRow--scroll overflow-x-auto justify-start snap-x snap-mandatory' : 'mobileTabRow--wrap justify-around'} flex items-center px-3 ${className ?? ''}`}
        role="tablist"
        aria-label={ariaLabel}
        data-testid={testId}
        data-variant="underline"
        data-placement={placement}
        data-overflow-strategy={overflowStrategy}
      >
        {items.map((item, index) => {
          const isActive = item.key === activeKey
          const hasCount = typeof item.count === 'number'
          return (
            <Pressable
              key={item.key}
              ref={(el) => {
                tabRefs.current[index] = el
              }}
              role="tab"
              onClick={() => onChange(item.key)}
              onKeyDown={(e) => handleTabKeyDown(e, index)}
              aria-selected={isActive}
              aria-label={item.label}
              data-testid={item.testId}
              className={`relative flex items-center justify-center gap-1.5 px-3 py-2.5 text-xs transition-colors border-b-2 -mb-[1px] ${scrollMode ? 'flex-[1_0_auto] min-w-max snap-center' : 'flex-1'} ${
                isActive
                  ? 'border-[var(--accent)] text-[var(--text-strong)] font-semibold'
                  : 'border-transparent text-[var(--muted)] hover:text-[var(--text-strong)] font-normal'
              }`}
            >
              {item.icon ? (
                <span className="inline-flex items-center justify-center" aria-hidden="true">
                  {isActive ? item.activeIcon ?? item.icon : item.icon}
                </span>
              ) : null}
              <span className="truncate">{item.label}</span>
              {hasCount ? (
                <span
                  className={`inline-flex items-center justify-center min-w-[18px] h-4 px-1 text-[10px] font-semibold rounded-full ${
                    isActive
                      ? 'bg-[var(--accent-soft)] text-[var(--accent-strong)] border border-[color-mix(in_srgb,var(--accent)_35%,transparent)]'
                      : 'bg-[var(--surface-raised)] text-[var(--muted)] border border-[var(--border)]'
                  }`}
                  style={{ fontFamily: 'var(--font-figures)', fontVariantNumeric: 'tabular-nums' }}
                  data-testid={item.testId ? `${item.testId}-count` : undefined}
                >
                  {item.count}
                </span>
              ) : null}
            </Pressable>
          )
        })}
      </div>
    )
  }

  return (
  <div
    className={`mobileTabRow ${isBottom ? 'mobileTabRow--placement-bottom border-t border-[var(--border)]' : ''} ${scrollMode ? 'mobileTabRow--scroll' : 'mobileTabRow--wrap'} flex items-center ${scrollMode ? 'justify-start overflow-x-auto snap-x snap-mandatory' : 'justify-around'} px-3 ${className?.includes('py-') ? '' : 'py-0'} ${className ?? ''}`}
    role="tablist"
    aria-label={ariaLabel}
    data-testid={testId}
    data-placement={placement}
    data-overflow-strategy={overflowStrategy}
  >
    {items.map((item, index) => {
      const isActive = item.key === activeKey
      const hasUnread = !isActive && item.unread
      return (
        <Pressable
          key={item.key}
          ref={(el) => {
            tabRefs.current[index] = el
          }}
          role="tab"
          onClick={() => {
            if (suppressClick.current) return
            onChange(item.key)
          }}
          onKeyDown={(e) => handleTabKeyDown(e, index)}
          onPointerDown={(event) => {
            if (!item.onLongPress || event.button !== 0) return
            suppressSelection(event.currentTarget)
            longPressStart.current = { x: event.clientX, y: event.clientY }
            longPressTimer.current = setTimeout(() => {
              longPressTimer.current = null
              suppressClick.current = true
              item.onLongPress?.()
            }, 500)
          }}
          onPointerMove={(event) => {
            const start = longPressStart.current
            if (!start) return
            if (Math.hypot(event.clientX - start.x, event.clientY - start.y) > 8) clearLongPress()
          }}
          onPointerUp={clearLongPress}
          onPointerCancel={clearLongPress}
          onPointerLeave={clearLongPress}
          aria-selected={isActive}
          aria-label={item.label}
          data-testid={item.testId}
          className={`${scrollMode ? 'flex-[1_0_auto] min-w-[5.3rem] snap-center' : 'flex-1 min-w-0'} ${slim ? 'h-11 gap-0' : compact ? 'h-12' : 'h-14'} flex flex-col items-center justify-center group`}
        >
          <span
            // §8.0.4 / §1.1 — Mosaic accent pill active tab indicator.
            className={`relative inline-flex items-center justify-center ${slim ? 'h-7 w-12' : 'h-9 w-14'} rounded-full transition-all duration-200 ease-[cubic-bezier(0.16,1,0.3,1)] ${
              isActive
                ? 'bg-[var(--accent-soft)] text-[var(--accent-strong)] border border-[color-mix(in_srgb,var(--accent)_40%,transparent)] scale-100'
                : 'text-[var(--muted)] group-hover:text-[var(--text-strong)] scale-95'
            }`}
          >
            <span
              className="inline-flex items-center justify-center"
              style={{ filter: 'none' }}
            >
              {isActive ? item.activeIcon ?? item.icon : item.icon}
            </span>
            {hasUnread ? (
              <span
                className="absolute -top-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-[var(--success)] ring-1 ring-[var(--surface-card)]"
                aria-label={unreadDotAria}
                data-testid={item.testId ? `${item.testId}-unread` : undefined}
              />
            ) : null}
          </span>
          <OverflowTip label={item.label}>
            <span
              className={`max-w-full truncate px-0.5 text-[11px] tracking-normal ${
                isActive ? 'font-semibold text-[var(--text-strong)]' : 'font-normal text-[var(--muted)] group-hover:text-[var(--text-strong)]'
              }`}
            >
              {item.label}
            </span>
          </OverflowTip>
        </Pressable>
      )
    })}
    {more ? (
      <MobileTabMoreButton
        more={more}
        scrollMode={scrollMode}
        slim={slim}
        compact={compact}
        unreadAriaLabel={unreadDotAria}
      />
    ) : null}
  </div>
  )
}

// §8.0.2 — `position: fixed` escapes any ancestor's containing block, EXCEPT
// when an ancestor has `transform`, `filter`, `perspective`, or
// `will-change` set (those establish a new containing block and re-anchor
// `fixed` to the ancestor rectangle instead of the viewport). Our member
// shell has framer-motion transforms in the boot splash + raid overlay path
// which was making the bottom nav render inside the tall document flow
// instead of pinning to the viewport. Portalling to `document.body` bypasses
// the entire ancestor chain so `fixed` behaves exactly like the DOM spec.
const useBodyPortalTarget = (): HTMLElement | null => {
  const [target] = useState<HTMLElement | null>(() => (typeof document === 'undefined' ? null : document.body))
  return target
}

/**
 * Fixed bottom container for one or two `MobileTabRow`s. Render the secondary
 * row first (it sits on top), then the primary row. Portalled to document.body
 * so ancestor transforms/filters can't neutralize `position: fixed`.
 */
export const MobileTabBar = ({
  children,
  ariaLabel,
  testId,
  hasSecondaryRow = false,
  addon,
  onHeightChange,
  surface,
  layer = 'app',
}: {
  children?: ReactNode
  ariaLabel: string
  testId?: string
  hasSecondaryRow?: boolean
  addon?: ReactNode
  onHeightChange?: (height: number) => void
  /** Stamped as `data-surface` on the portalled `<nav>` so CSS can scope
   *  rules to a specific surface without going through ancestor selectors
   *  (the nav is portalled to body, so ancestor selectors don't reach it). */
  surface?: string
  layer?: 'app' | 'shell'
}) => {
  const target = useBodyPortalTarget()
  const navRef = useRef<HTMLElement>(null)

  useEffect(() => {
    const el = navRef.current
    if (!el || typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const height = entry.borderBoxSize?.[0]?.blockSize ?? entry.contentRect.height
        if (typeof height === 'number' && height > 0) {
          onHeightChange?.(height)
        }
      }
    })
    ro.observe(el)
    return () => ro.disconnect()
  }, [onHeightChange])

  const isShellLayer = layer === 'shell'
  const bar = (
    <nav
      ref={navRef}
      // mosaic-keep: glass — phone tab bar
      className={`fixed bottom-0 left-0 right-0 ${isShellLayer ? '' : 'z-30 '}bg-[color-mix(in_srgb,var(--surface-card)_90%,transparent)] backdrop-blur-3xl border-t border-[var(--border)] flex flex-col divide-y divide-[var(--border)]`}
      aria-label={ariaLabel}
      data-testid={testId}
      data-has-secondary-row={hasSecondaryRow ? 'true' : 'false'}
      data-surface={surface}
      style={{
        paddingBottom: 'calc(env(safe-area-inset-bottom, 0px))',
        ...(isShellLayer ? { zIndex: 'var(--z-rail)' } : {}),
      }}
    >
      {addon}
      {children}
    </nav>
  )
  // SSR / pre-mount fallback: render inline so the initial paint isn't blank.
  if (!target) return bar
  return createPortal(bar, target)
}
